import { Alert, Button, Input, Modal, Pagination, Select, Tabs, Tag, Tooltip } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Image as ImageIcon, ShieldAlert, ShieldQuestion } from 'lucide-react';
import { EmptyState } from '../EmptyState';
import { MediaUpload, filesUploader, type MediaUploadValue } from '../MediaUpload';
import { resolveAssetUrl } from '@shared/utils/assetUrl';
import { useDebounce } from '@shared/hooks/useDebounce';
import { filesApi, fileEmbedUrl, type FileItem, type FileScanStatus } from '@api/files';
import { foldersApi } from '@api/folders';
import { flattenFolders } from '@features/media/utils';
import { isSelectable, uploadSelectable } from './scanGate';

export interface MediaItem {
  id: string;
  url: string;
  name: string;
  alt?: string;
  type?: 'image' | 'video' | 'doc';
  size?: number;
  uploadedAt?: string;
}

interface MediaPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (item: MediaItem) => void;
  multi?: boolean;
  onSelectMany?: (items: MediaItem[]) => void;
}

const PAGE_SIZE = 24;

function toMediaItem(file: FileItem): MediaItem {
  return {
    id: file.id,
    url: fileEmbedUrl(file),
    name: file.fileName,
    alt: file.altText ?? undefined,
    size: file.sizeBytes,
    uploadedAt: file.createdAt,
  };
}

function tileImageUrl(file: FileItem): string | null {
  return file.variants?.thumb?.url ?? file.thumbnailUrl ?? file.publicUrl ?? null;
}

export function MediaPicker({ open, onClose, onSelect, multi, onSelectMany }: MediaPickerProps) {
  const { t } = useTranslation(['components', 'common', 'media']);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const [folderId, setFolderId] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);
  const [newUpload, setNewUpload] = useState<MediaUploadValue | null>(null);

  const { data: folderTree = [] } = useQuery({
    queryKey: ['folders', 'tree'],
    queryFn: () => foldersApi.tree(),
    enabled: open,
    staleTime: 60_000,
  });

  const { data: filesResult, isLoading } = useQuery({
    queryKey: ['files', 'picker', folderId ?? null, debouncedQuery, page],
    queryFn: () =>
      filesApi.list({
        folderId: folderId ?? null,
        search: debouncedQuery || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: open,
  });

  const folderOptions = useMemo(
    () => [
      { value: '', label: t('media:folderTree.allFiles', 'Tüm dosyalar') },
      ...flattenFolders(folderTree).map((f) => ({
        value: f.id,
        label: `${' '.repeat(f.depth * 2)}${f.name}`,
      })),
    ],
    [folderTree, t],
  );

  // Taze yüklenen dosyanın gerçek tarama durumu (id upload dönüşünden gelir); Pending
  // iken kısa aralıkla yoklar, terminal duruma (Clean/Infected/…) ulaşınca durur.
  const uploadFileId = newUpload?.id;
  const uploadScanQuery = useQuery({
    queryKey: ['files', 'scan', uploadFileId],
    queryFn: () => filesApi.get(uploadFileId ?? ''),
    enabled: open && !!uploadFileId,
    refetchInterval: (q) => (q.state.data?.scanStatus === 'Pending' ? 2500 : false),
  });
  const uploadScanStatus = uploadFileId ? uploadScanQuery.data?.scanStatus : undefined;
  const canPickUpload = uploadSelectable(newUpload, uploadScanStatus);

  const files = filesResult?.items ?? [];
  const total = filesResult?.total ?? 0;

  const resetAndClose = () => {
    setSelected([]);
    setNewUpload(null);
    onClose();
  };

  const confirmLibrary = () => {
    const chosen = files.filter((f) => selected.includes(f.id)).map(toMediaItem);
    const first = chosen[0];
    if (!first) return;
    if (multi) onSelectMany?.(chosen);
    else onSelect(first);
    resetAndClose();
  };

  const toggle = (file: FileItem) => {
    if (!isSelectable(file.scanStatus)) return;
    if (multi) {
      setSelected((prev) =>
        prev.includes(file.id) ? prev.filter((x) => x !== file.id) : [...prev, file.id],
      );
    } else {
      setSelected([file.id]);
    }
  };

  const handleUploadSelect = () => {
    if (!newUpload || !canPickUpload) return;
    onSelect({
      id: `new-${Date.now()}`,
      url: newUpload.url,
      name: newUpload.name ?? t('mediaPicker.newMediaName'),
      alt: newUpload.alt,
    });
    resetAndClose();
  };

  return (
    <Modal
      open={open}
      onCancel={resetAndClose}
      title={t('mediaPicker.title')}
      width={880}
      destroyOnHidden
      footer={null}
    >
      <Tabs
        items={[
          {
            key: 'library',
            label: t('mediaPicker.library'),
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Select
                    style={{ minWidth: 200 }}
                    value={folderId ?? ''}
                    onChange={(v) => {
                      setFolderId(v || undefined);
                      setPage(1);
                    }}
                    options={folderOptions}
                    showSearch
                    optionFilterProp="label"
                  />
                  <Input.Search
                    placeholder={t('mediaPicker.searchPlaceholder')}
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setPage(1);
                    }}
                    allowClear
                    style={{ flex: 1, minWidth: 180 }}
                  />
                </div>

                {isLoading ? (
                  <div style={{ padding: 32, textAlign: 'center', color: 'var(--ds-text-tertiary)' }}>
                    {t('mediaPicker.loading')}
                  </div>
                ) : files.length === 0 ? (
                  <EmptyState
                    icon={<ImageIcon size={32} />}
                    title={t('mediaPicker.emptyTitle')}
                    description={t('mediaPicker.emptyDescription')}
                  />
                ) : (
                  <div className="ds-media-grid">
                    {files.map((file) => {
                      const isSel = selected.includes(file.id);
                      const selectable = isSelectable(file.scanStatus);
                      const thumb = tileImageUrl(file);
                      return (
                        <button
                          key={file.id}
                          type="button"
                          className="ds-media-tile"
                          data-selected={isSel}
                          disabled={!selectable}
                          onClick={() => toggle(file)}
                          onDoubleClick={() => {
                            if (!selectable) return;
                            onSelect(toMediaItem(file));
                            resetAndClose();
                          }}
                          aria-pressed={isSel}
                          style={selectable ? undefined : { opacity: 0.55, cursor: 'not-allowed' }}
                        >
                          {thumb ? (
                            <img src={resolveAssetUrl(thumb)} alt={file.altText ?? file.fileName} />
                          ) : (
                            <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
                              <ImageIcon size={28} />
                            </div>
                          )}
                          <ScanBadge status={file.scanStatus} />
                          <div className="ds-media-tile__caption">{file.fileName}</div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {total > PAGE_SIZE ? (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Pagination
                      current={page}
                      pageSize={PAGE_SIZE}
                      total={total}
                      onChange={setPage}
                      size="small"
                      showSizeChanger={false}
                    />
                  </div>
                ) : null}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <Button onClick={resetAndClose}>{t('common:actions.cancel')}</Button>
                  <Button type="primary" disabled={selected.length === 0} onClick={confirmLibrary}>
                    {multi
                      ? t('mediaPicker.selectCount', { count: selected.length })
                      : t('mediaPicker.select')}
                  </Button>
                </div>
              </div>
            ),
          },
          {
            key: 'upload',
            label: t('mediaPicker.upload'),
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <MediaUpload value={newUpload} onChange={setNewUpload} uploader={filesUploader} />
                {newUpload && !canPickUpload ? (
                  uploadScanStatus === 'Infected' || uploadScanStatus === 'Failed' ? (
                    <Alert
                      type="error"
                      showIcon
                      message={t('mediaPicker.scanBlocked')}
                      data-testid="media-picker-scan-blocked"
                    />
                  ) : (
                    <Alert
                      type="info"
                      showIcon
                      message={t('mediaPicker.scanning')}
                      data-testid="media-picker-scanning"
                    />
                  )
                ) : null}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <Button onClick={resetAndClose}>{t('common:actions.cancel')}</Button>
                  <Button
                    type="primary"
                    disabled={!newUpload || !canPickUpload}
                    onClick={handleUploadSelect}
                  >
                    {t('mediaPicker.selectAndClose')}
                  </Button>
                </div>
              </div>
            ),
          },
        ]}
      />
    </Modal>
  );
}

function ScanBadge({ status }: { status: FileScanStatus }) {
  const { t } = useTranslation('media');
  if (status === 'Infected' || status === 'Failed') {
    return (
      <Tooltip title={t('scan.infectedHint')}>
        <Tag
          color="error"
          icon={<ShieldAlert size={11} />}
          style={{ position: 'absolute', top: 4, left: 4, margin: 0 }}
        />
      </Tooltip>
    );
  }
  if (status === 'Pending') {
    return (
      <Tooltip title={t('scan.pendingHint')}>
        <Tag
          color="warning"
          icon={<ShieldQuestion size={11} />}
          style={{ position: 'absolute', top: 4, left: 4, margin: 0 }}
        />
      </Tooltip>
    );
  }
  return null;
}
